/**
 * Box 7 (spec §5.6, plan R27): mROAS at the current budget with its range across draws, and the prototype's moveAdvice
 * on those ranges. Nothing is recommended for a stopped channel, and a red quality tile (spec §5.2) gives only
 * `quality`. Pure.
 *
 * @module services/budget/advice
 */
import { QUALITY } from './budgetContract';
import type { Bundle, Ranged } from './bundleSchema';
import { rangedOf, slopeAt } from './curves';
import { autoAllocate, currentAllocation, drawTotals, forecastOf, MIN_MOVE, type Allocation } from './simulator';

type Shift = { channel: string; delta: number };

export type AdviceItem =
  | {
    kind: 'move';
    /** The lowest and the highest mean mROAS among running channels: the pair whose ranges part (R27). */
    from: string;
    to: string;
    /** What auto takes out of `from`. */
    amount: number;
    fromMarginal: Ranged;
    toMarginal: Ranged;
    /** The whole auto reallocation's gain over today, and that as a % of today's media outcome. */
    gain: Ranged;
    gainPct: number;
    /**
     * Where auto moves the budget: auto − current for each running channel that moves by MIN_MOVE or more, the
     * increases largest first, then the cuts largest first (T9g). `to` may take only part of `amount`, since its own
     * +MAX_SHIFT caps it (T9c).
     */
    shifts: readonly Shift[];
  }
  | { kind: 'notYet'; channel: string; marginal: Ranged; overlaps: readonly string[] }
  | { kind: 'stopped'; channel: string; lastActiveWeek: string }
  | { kind: 'quality' };

interface Running {
  channel: string;
  marginal: Ranged;
}

/** Spec §5.6: per draw, the slope of the draw's curve at the channel's current weekly budget. Running channels only. */
export const marginalAtCurrent = (b: Bundle): Readonly<Record<string, Ranged>> =>
  Object.fromEntries(b.channels.filter((c) => !c.stopped).map((c) => {
    const { grid, mean, draws } = b.curves[c.id];
    const x = b.current_weekly[c.id];
    return [c.id, rangedOf(slopeAt(grid, mean, x), draws.map((ys) => slopeAt(grid, ys, x)))];
  }));

/** Spec §5.2's tiles, all green. r-hat is strict (spec C9): 1.2 itself is red. */
export const qualityOk = (d: Bundle['diagnostics']): boolean =>
  d.r2 >= QUALITY.minR2 && d.mape <= QUALITY.maxMape && d.max_rhat < QUALITY.maxRhat && d.divergences <= QUALITY.maxDivergences;

const overlaps = (a: Ranged, b: Ranged): boolean => !(a.hi < b.lo || b.hi < a.lo);

// A stopped channel's current and auto are both 0, so only running channels pass the MIN_MOVE filter.
const shiftsOf = (b: Bundle, current: Allocation, auto: Allocation): Shift[] =>
  b.channels.map((c) => ({ channel: c.id, delta: auto[c.id] - current[c.id] }))
    .filter((s) => Math.abs(s.delta) >= MIN_MOVE)
    .sort((x, y) => Math.sign(y.delta) - Math.sign(x.delta) || Math.abs(y.delta) - Math.abs(x.delta));

/** The move from the lowest mean mROAS to the highest: only when their ranges part and auto takes budget out of the lowest. */
const moveOf = (b: Bundle, from: Running, to: Running): AdviceItem | null => {
  if (!(from.marginal.hi < to.marginal.lo)) return null;
  const current = currentAllocation(b);
  const auto = autoAllocate(b);
  const amount = Math.max(0, current[from.channel] - auto[from.channel]);
  const base = forecastOf(b, current).outcome.mean;
  // A base that prints 0.00 is 0, so there is no percentage of it (T9f).
  if (amount < MIN_MOVE || !(base >= MIN_MOVE)) return null;
  const now = drawTotals(b, current);
  const mean = forecastOf(b, auto).outcome.mean - base;
  // Paired: draw d at auto minus the same draw at current, never one range minus the other.
  const gain = rangedOf(mean, drawTotals(b, auto).map((v, d) => v - now[d]));
  return {
    kind: 'move', from: from.channel, to: to.channel, amount,
    fromMarginal: from.marginal, toMarginal: to.marginal, gain, gainPct: (mean / base) * 100, shifts: shiftsOf(b, current, auto),
  };
};

/** The move, then notYet from the lowest mean mROAS up, then stopped channels in the bundle's order. */
export const adviceOf = (b: Bundle): readonly AdviceItem[] => {
  if (!qualityOk(b.diagnostics)) return [{ kind: 'quality' }];
  const m = marginalAtCurrent(b);
  // sort is stable, so a tie keeps the bundle's order.
  const running: Running[] = b.channels.filter((c) => !c.stopped).map((c) => ({ channel: c.id, marginal: m[c.id] }))
    .sort((x, y) => x.marginal.mean - y.marginal.mean);
  const from = running[0];
  const to = running[running.length - 1];
  const move = running.length > 1 ? moveOf(b, from, to) : null;
  const notYet = running
    .filter((r) => move === null || (r !== from && r !== to))
    .flatMap((r): AdviceItem[] => {
      const overlapping = running.filter((o) => o !== r && overlaps(r.marginal, o.marginal)).map((o) => o.channel);
      return overlapping.length > 0 ? [{ kind: 'notYet', channel: r.channel, marginal: r.marginal, overlaps: overlapping }] : [];
    });
  const stopped = b.channels.filter((c) => c.stopped)
    .map((c): AdviceItem => ({ kind: 'stopped', channel: c.id, lastActiveWeek: c.last_active_week }));
  return [...(move === null ? [] : [move]), ...notYet, ...stopped];
};
